import { useCallback, useContext, useEffect, useRef } from 'react'
import { Logger, MINUTE, SECOND, isResponseError } from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { HeadersContext, type Stage, useAddUserToken, useBackend } from '@front/model'
import { resolveTopicToQueryKeys, type TopicInvalidation } from './topicResolver'

const logger = new Logger('socket/invalidation')

const INITIAL_BACKOFF_MS = SECOND
const MAX_BACKOFF_MS = MINUTE
const MAX_RECONNECT_ATTEMPTS = 10
const HEARTBEAT_INTERVAL_MS = 9 * MINUTE

function getSocketUrl(stage: Stage): string {
  const baseUrl = stage === 'prod' ? 'terros.com' : `${stage}.terros.com`
  return `wss://socket.api.${baseUrl}/ws`
}

export function useSocketInvalidation(): void {
  const backend = useBackend()
  const { add: addUserToken } = useAddUserToken()
  const queryClient = useQueryClient()
  const headers = useContext(HeadersContext)
  const sessionId = headers?.['terros-session']

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttemptRef = useRef(0)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const shouldConnectRef = useRef(true)
  const socketDisabledRef = useRef(false)

  const invalidateTopics = useCallback(
    (invalidations: TopicInvalidation[]): void => {
      const seen = new Set<string>()

      for (const invalidation of invalidations) {
        const queryKeys = resolveTopicToQueryKeys(invalidation)
        for (const queryKey of queryKeys) {
          const keyStr = JSON.stringify(queryKey)
          if (seen.has(keyStr)) continue
          seen.add(keyStr)
          queryClient.invalidateQueries({ queryKey })
        }
      }
    },
    [queryClient]
  )

  const clearTimers = useCallback((): void => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current)
      heartbeatTimerRef.current = null
    }
  }, [])

  const disableSocket = useCallback(
    (reason: string, error?: unknown): void => {
      socketDisabledRef.current = true
      clearTimers()

      if (error) {
        logger.warn(`Disabling WebSocket invalidation: ${reason}`, error)
        return
      }

      logger.warn(`Disabling WebSocket invalidation: ${reason}`)
    },
    [clearTimers]
  )

  const disableForTokenError = useCallback(
    (error: unknown): boolean => {
      if (!isResponseError(error)) return false
      if (error.error !== 'NotAuthorized' && error.error !== 'Deactivated') return false

      disableSocket(error.message ?? error.error, error)
      return true
    },
    [disableSocket]
  )

  const connect = useCallback(
    async (userToken: string): Promise<void> => {
      if (!sessionId) return
      if (socketDisabledRef.current) return

      const stage = backend.getStage()
      const baseUrl = getSocketUrl(stage)

      const params = new URLSearchParams({
        userToken,
        terrosSession: sessionId,
        transportType: 'ws',
      })

      const url = `${baseUrl}?${params.toString()}`

      try {
        const ws = new WebSocket(url)
        wsRef.current = ws

        ws.onopen = (): void => {
          logger.info('WebSocket connected')
          reconnectAttemptRef.current = 0

          heartbeatTimerRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              try {
                ws.send(JSON.stringify({ action: 'heartbeat' }))
              } catch (error) {
                logger.warn('Failed to send WebSocket heartbeat', error)
              }
            }
          }, HEARTBEAT_INTERVAL_MS)
        }

        ws.onmessage = (event: MessageEvent): void => {
          try {
            const data: unknown = JSON.parse(String(event.data))
            if (!Array.isArray(data)) return
            const invalidations = data as TopicInvalidation[]
            logger.info(`Received ${invalidations.length} topic invalidations`)
            invalidateTopics(invalidations)
          } catch {
            logger.warn('Failed to parse WebSocket message')
          }
        }

        ws.onclose = (event: CloseEvent): void => {
          logger.info(`WebSocket disconnected with code ${event.code}`)
          wsRef.current = null
          if (heartbeatTimerRef.current) {
            clearInterval(heartbeatTimerRef.current)
            heartbeatTimerRef.current = null
          }

          if (event.code === 1008) {
            disableSocket('websocket authorization rejected')
            return
          }

          scheduleReconnect()
        }

        ws.onerror = (): void => {
          logger.warn('WebSocket error')
        }
      } catch {
        logger.warn('Failed to create WebSocket connection')
        scheduleReconnect()
      }
    },
    [backend, disableSocket, sessionId, invalidateTopics]
  )

  const scheduleReconnect = useCallback((): void => {
    if (!shouldConnectRef.current) return
    if (socketDisabledRef.current) return
    if (reconnectTimerRef.current) return

    const attempt = reconnectAttemptRef.current + 1
    if (attempt > MAX_RECONNECT_ATTEMPTS) {
      logger.warn('Max reconnect attempts reached')
      return
    }

    reconnectAttemptRef.current = attempt
    const backoff = Math.min(INITIAL_BACKOFF_MS * Math.pow(2, attempt - 1), MAX_BACKOFF_MS)
    logger.info(`Reconnecting in ${backoff}ms (attempt ${attempt}/${MAX_RECONNECT_ATTEMPTS})`)

    reconnectTimerRef.current = setTimeout(async () => {
      reconnectTimerRef.current = null
      try {
        const userToken = await addUserToken()
        if (!shouldConnectRef.current) return
        await connect(userToken)
      } catch (error) {
        if (disableForTokenError(error)) return
        logger.warn('Failed to create WebSocket user token', error)
        scheduleReconnect()
      }
    }, backoff)
  }, [addUserToken, connect, disableForTokenError])

  useEffect(() => {
    shouldConnectRef.current = true
    socketDisabledRef.current = false

    const start = async (): Promise<void> => {
      try {
        const userToken = await addUserToken()
        if (!shouldConnectRef.current) return
        await connect(userToken)
      } catch (error) {
        if (disableForTokenError(error)) return
        logger.warn('Failed to create initial WebSocket user token', error)
        scheduleReconnect()
      }
    }

    void start()

    return (): void => {
      shouldConnectRef.current = false
      socketDisabledRef.current = true
      clearTimers()
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [clearTimers, disableForTokenError])
}
